'use client';

import { useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { faqData } from '@/app/_data/faq';
import { cn } from '@/app/_utils/cn';

export interface IFaqAccordionItem {
  question: string;
  answer: string;
}

interface IFaqSectionProps extends Omit<ComponentPropsWithoutRef<'section'>, 'children'> {
  /** Omit to use landing `faqData`. */
  items?: readonly IFaqAccordionItem[];
  variant?: 'home' | 'blog';
  /** Which row is expanded on mount; `null` = 모두 접힘 */
  defaultOpenIndex?: number | null;
}

const FALLBACK_ITEMS = faqData.map((item): IFaqAccordionItem => ({
  question: item.q,
  answer: item.a,
}));

const FAQSection = ({
  items,
  variant = 'home',
  defaultOpenIndex = null,
  className,
  ...sectionProps
}: IFaqSectionProps) => {
  const resolvedItems = items ?? FALLBACK_ITEMS;

  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpenIndex ?? null);

  const maxOpenHeightPx = 2000;

  const titleId =
    variant === 'blog'
      ? (sectionProps.id ? `${sectionProps.id}-title` : 'blog-faq-heading')
      : undefined;

  const content = (
    <>
      {variant === 'home' ? (
        <>
          <div className='text-xs font-bold uppercase tracking-[0.12em] text-primary mb-3.5'>FAQ</div>
          <h2
            className='font-display font-black leading-[1.05] tracking-[-0.04em]'
            style={{ fontSize: 'clamp(32px, 4.5vw, 52px)' }}
          >
            Frequently asked <span className='gradient-text'>questions</span>
          </h2>
        </>
      ) : (
        <h2
          id={titleId ?? 'blog-faq-heading'}
          className={cn(
            'mx-auto mb-7 max-w-[min(52rem,calc(100%-0.25rem))] w-full sm:mb-8',
            'text-[clamp(1.35rem,3.5vw,1.75rem)] font-bold tracking-[-0.03em] text-foreground',
          )}
        >
          Frequently Asked Questions
        </h2>
      )}

      <div
        className={cn(
          'flex flex-col',
          variant === 'home' ? 'gap-2' : 'gap-3',
          variant === 'home'
            ? 'max-w-[700px] mx-auto mt-12'
            : 'mx-auto mt-10 w-full max-w-[min(52rem,calc(100%-0.25rem))] sm:mt-11',
        )}
      >
        {resolvedItems.map((item, i) => {
          const isOpen = openIndex === i;

          const cardRounded = variant === 'blog' ? 'rounded-[16px] sm:rounded-[18px]' : 'rounded-2xl';

          return (
            <div
              key={`${variant}-${item.question.slice(0, 48)}-${i}`}
              className={cn(
                'bg-card border overflow-hidden transition-[border-color,box-shadow] duration-200 motion-reduce:duration-150',
                cardRounded,
                isOpen &&
                  (variant === 'blog'
                    ? 'border-primary/35 shadow-sm'
                    : 'border-[hsl(var(--border-light))]'),
                !isOpen && 'border-border',
              )}
            >
              <button
                type='button'
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${variant}-${i}`}
                id={`faq-trigger-${variant}-${i}`}
                className={cn(
                  'w-full bg-transparent border-none text-left flex items-start justify-between cursor-pointer shrink-0',
                  variant === 'home'
                    ? 'text-foreground px-6 py-[22px] font-display text-base font-bold'
                    : 'text-foreground px-4 py-4 sm:px-5 sm:py-[22px] text-base font-semibold leading-snug sm:text-[1.0625rem] sm:tracking-[-0.02em]',
                )}
              >
                <span className='min-w-0 flex-1 pr-3 pt-px'>{item.question}</span>
                <span
                  aria-hidden
                  className={cn(
                    'text-[22px] text-muted-foreground transition-transform ml-4 shrink-0 font-normal',
                    variant === 'blog' ? 'motion-reduce:transition-none' : '',
                  )}
                  style={{ transform: isOpen ? 'rotate(45deg)' : 'none' }}
                >
                  +
                </span>
              </button>
              <div
                id={`faq-panel-${variant}-${i}`}
                role='region'
                aria-labelledby={`faq-trigger-${variant}-${i}`}
                className='overflow-hidden transition-[max-height] duration-200 motion-reduce:duration-150 ease-out'
                style={{ maxHeight: isOpen ? maxOpenHeightPx : 0 }}
              >
                <div
                  className={cn(
                    'text-left',
                    variant === 'home'
                      ? 'px-6 pb-[22px] text-[15px] text-[hsl(var(--text-secondary))] leading-[1.7]'
                      : 'max-w-[min(42rem,calc(100vw-3rem))] px-4 pb-5 sm:px-6 sm:pb-[22px] text-[0.9375rem] leading-[1.72] text-muted-foreground sm:text-base sm:leading-relaxed',
                  )}
                >
                  <p>{item.answer}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const sectionAria = variant === 'blog' ? { 'aria-labelledby': titleId ?? 'blog-faq-heading' as const } : {};

  const sectionCn = cn(
    variant === 'home'
      ? 'py-[120px] px-6 max-w-[1100px] mx-auto text-center'
      : 'faq-section-blog not-prose scroll-mt-10 w-full px-4 sm:px-0 max-w-none text-left',
    className,
  );

  return (
    <section
      {...sectionProps}
      {...sectionAria}
      lang={variant === 'blog' ? 'en' : undefined}
      className={sectionCn}
    >
      {content}
    </section>
  );
};

FAQSection.displayName = 'FAQSection';

export default FAQSection;
